import React, { useState, useMemo, useEffect } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle2,
  ChevronDown,
  CircleAlert,
  Plus,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  UserRoundX,
  UsersRound,
  X,
} from 'lucide-react';
import { useAdminUsers } from '../hooks/useAdminUsers';
import { User } from '../models/User';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faBan, faCheck, faSpinner } from '@fortawesome/free-solid-svg-icons';

interface UserManagementProps {}

const filterControlClassName = 'block h-11 w-full rounded-xl border border-slate-200 bg-slate-50/70 px-3 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 focus-visible:border-blue-500 focus-visible:bg-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60';

export const UserManagement: React.FC<UserManagementProps> = () => {
  // Use the admin users hook
  const {
    adminUsers: users,
    loading,
    error,
    pagination,
    stats,
    refreshData,
    updateFilters,
    createAdminUser,
    updateUserStatus,
    changeUserPassword
  } = useAdminUsers();

  // Initialize state from URL parameters
  const getInitialState = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      searchTerm: urlParams.get('search') || '',
      statusFilter: (urlParams.get('status') as 'all' | 'enabled' | 'disabled') || 'all',
      currentPage: parseInt(urlParams.get('page') || '1', 10),
      itemsPerPage: parseInt(urlParams.get('limit') || '10', 10),
    };
  };

  const initialState = getInitialState();

  // State management
  const [searchTerm, setSearchTerm] = useState(initialState.searchTerm);
  const [statusFilter, setStatusFilter] = useState(initialState.statusFilter);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof User | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(initialState.currentPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialState.itemsPerPage);
  const [areFiltersOpen, setAreFiltersOpen] = useState(false);
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [filterError, setFilterError] = useState<string | null>(null);

  // Update URL parameters when filters change
  const updateURL = (filters: any) => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    
    // Update URL parameters
    if (filters.page && filters.page > 1) {
      params.set('page', filters.page.toString());
    } else {
      params.delete('page');
    }
    
    if (filters.limit && filters.limit !== 10) {
      params.set('limit', filters.limit.toString());
    } else {
      params.delete('limit');
    }
    
    if (filters.search) {
      params.set('search', filters.search);
    } else {
      params.delete('search');
    }
    
    if (filters.status && filters.status !== 'all') {
      params.set('status', filters.status);
    } else {
      params.delete('status');
    }
    
    // Update browser URL without page reload
    const newURL = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    window.history.replaceState({}, '', newURL);
  };

  // Form state for adding new user
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'user' as 'user' | 'moderator',
    status: 'enabled'
  });
  const [formErrors, setFormErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    password?: string;
  }>({});

  // User details modal state
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    status: 'Active' as 'Active' | 'Disabled'
  });
  const [newPassword, setNewPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isErrorMessage, setIsErrorMessage] = useState(false);
  
  // Loading state for status changes
  const [statusChangeLoading, setStatusChangeLoading] = useState<{ [userId: string]: boolean }>({});

  // Calculate stats from hook data or use local calculation as fallback
  const calculatedStats = useMemo(() => {
    if (stats) {
      return {
        totalUsers: stats.totalUsers || users.length,
        activeUsers: stats.enabledUsers || users.filter(user => user.accountStatus === 'enabled').length,
        disabledUsers: stats.disabledUsers || users.filter(user => user.accountStatus === 'disabled').length
      };
    }
    
    const totalUsers = users.length;
    const activeUsers = users.filter(user => user.accountStatus === 'enabled').length;
    const disabledUsers = users.filter(user => user.accountStatus === 'disabled').length;
    
    return {
      totalUsers,
      activeUsers,
      disabledUsers
    };
  }, [users, stats]);


  // Filter and sort users
  const filteredAndSortedUsers = useMemo(() => {
    // If we have API pagination, use the API results directly
    if (pagination) {
      return users;
    }
    
    // Otherwise, apply local filtering
    let filtered = users.filter(user => {
      const matchesSearch = !searchTerm || searchTerm.trim().length === 0 || 
                          user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || user.accountStatus === statusFilter;
      return matchesSearch && matchesStatus;
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key!];
        const bValue = b[sortConfig.key!];
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [searchTerm, statusFilter, sortConfig, users, pagination]);

  // Use API pagination if available, otherwise fall back to local pagination
  const totalItems = pagination?.total || filteredAndSortedUsers.length;
  const totalPages = pagination?.totalPages || Math.ceil(filteredAndSortedUsers.length / itemsPerPage);
  const startIndex = pagination ? (pagination.page - 1) * pagination.limit : (currentPage - 1) * itemsPerPage;
  const endIndex = pagination ? startIndex + pagination.limit : startIndex + itemsPerPage;
  const paginatedUsers = pagination ? filteredAndSortedUsers : filteredAndSortedUsers.slice(startIndex, endIndex);
  const activeFilterCount = [Boolean(searchTerm.trim()), statusFilter !== 'all'].filter(Boolean).length;

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCurrentPage(1);
    setFilterError(null);
    const clearedFilters = { search: undefined, status: undefined, page: 1, limit: itemsPerPage };
    updateFilters(clearedFilters);
    updateURL(clearedFilters);
  };

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Handle search and filter changes by calling the API
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        // Clear any previous filter errors
        setFilterError(null);

        // Validate filter parameters
        const validatedFilters: any = {
          page: Math.max(1, currentPage), // Ensure page is at least 1
          limit: Math.min(100, Math.max(1, itemsPerPage)), // Limit between 1-100
        };

        // Add search filter if provided and not empty
        if (searchTerm && searchTerm.trim().length > 0) {
          if (searchTerm.trim().length < 2) {
            setFilterError('Search term must be at least 2 characters long');
            return;
          }
          validatedFilters.search = searchTerm.trim();
        } else {
          // Explicitly remove search filter when empty
          validatedFilters.search = undefined;
        }

        // Add status filter if not 'all'
        if (statusFilter && statusFilter !== 'all') {
          validatedFilters.status = statusFilter;
        }

        // Filter logging removed for security
        updateFilters(validatedFilters);
        updateURL(validatedFilters);
      } catch (error) {
        console.error('Error updating filters:', error);
        setFilterError('Failed to update filters. Please try again.');
      }
    }, 300); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [searchTerm, statusFilter, currentPage, itemsPerPage, updateFilters]);

  const handleSort = (key: keyof User) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getStatusBadge = (status: string) => {
    return status === 'enabled' 
      ? <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">Enabled</span>
      : <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700">Disabled</span>;
  };

  const getRoleBadge = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
        return <span className="inline-flex items-center rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700">Admin</span>;
      case 'moderator':
        return <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">Moderator</span>;
      case 'manager':
        return <span className="inline-flex items-center rounded-full border border-cyan-200 bg-cyan-50 px-2.5 py-1 text-xs font-semibold text-cyan-700">Manager</span>;
      case 'user':
        return <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700">System user</span>;
      default:
        return <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700">{role}</span>;
    }
  };

  const handleView = (user: User) => {
    setSelectedUser(user);
    setEditFormData({
      firstName: user.fullName.split(' ')[0] || '',
      lastName: user.fullName.split(' ').slice(1).join(' ') || '',
      email: user.email,
      status: user.accountStatus === 'enabled' ? 'Active' : 'Disabled'
    });
    setEditMode(false);
    setShowPasswordChange(false);
    setNewPassword('');
    setShowUserDetails(true);
  };

  // Form validation function
  const validateForm = () => {
    const errors: typeof formErrors = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password.trim()) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 12 || formData.password.length > 128) {
      errors.password = 'Password must be between 12 and 128 characters long';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form input changes
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Handle form submission
  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      // Prepare the API request payload
      const requestPayload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        status: formData.status as 'enabled' | 'disabled'
      };
      
      // User creation logging removed for security
      
      // Call the API through the hook
      await createAdminUser(requestPayload);
      
      // Reset form and close modal
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'user',
        status: 'enabled'
      });
      setFormErrors({});
      setShowAddUserForm(false);
      
      // Show success modal
      setSuccessMessage('User added successfully!');
      setIsErrorMessage(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error adding user:', error);
      setSuccessMessage('Failed to add user. Please try again.');
      setIsErrorMessage(true);
      setShowSuccessModal(true);
    }
  };

  // Handle edit mode toggle
  const toggleEditMode = () => {
    setEditMode(!editMode);
    if (!editMode) {
      // Reset to original data when entering edit mode
      if (selectedUser) {
        setEditFormData({
          firstName: selectedUser.fullName.split(' ')[0] || '',
          lastName: selectedUser.fullName.split(' ').slice(1).join(' ') || '',
          email: selectedUser.email,
          status: selectedUser.accountStatus === 'enabled' ? 'Active' : 'Disabled'
        });
      }
    }
  };

  // Handle edit form changes
  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle password change
  const handlePasswordChange = async () => {
    if (!currentPassword.trim()) {
      setSuccessMessage('Current password is required');
      setIsErrorMessage(true);
      setShowSuccessModal(true);
      return;
    }
    
    if (newPassword.length < 12 || newPassword.length > 128) {
      setSuccessMessage('New password must be between 12 and 128 characters long');
      setIsErrorMessage(true);
      setShowSuccessModal(true);
      return;
    }
    
    if (!selectedUser) {
      setSuccessMessage('No user selected');
      setIsErrorMessage(true);
      setShowSuccessModal(true);
      return;
    }
    
    try {
      const response = await changeUserPassword(selectedUser.id, currentPassword, newPassword);
      
      setNewPassword('');
      setCurrentPassword('');
      setShowPasswordChange(false);
      setSuccessMessage(response.message);
      setIsErrorMessage(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error changing password:', error);
      setSuccessMessage('Failed to change password. Please check your current password and try again.');
      setIsErrorMessage(true);
      setShowSuccessModal(true);
    }
  };

  // Handle save changes
  const handleSaveChanges = () => {
    if (!editFormData.firstName.trim() || !editFormData.lastName.trim() || !editFormData.email.trim()) {
      setSuccessMessage('Please fill in all required fields');
      setIsErrorMessage(true);
      setShowSuccessModal(true);
      return;
    }
    
    // TODO: In a real application, this would make an API call
    // User edit logging removed for security
    
    // Update the local user data (in a real app, this would come from API response)
    if (selectedUser) {
      const updatedUser = {
        ...selectedUser,
        fullName: `${editFormData.firstName} ${editFormData.lastName}`.trim(),
        email: editFormData.email,
        accountStatus: selectedUser.accountStatus
      };
      
      // Find and update the user in the users array
      const userIndex = users.findIndex(u => u.id === selectedUser.id);
      if (userIndex !== -1) {
        users[userIndex] = updatedUser;
        setSelectedUser(updatedUser);
      }
    }
    
    setEditMode(false);
    setSuccessMessage('Changes saved successfully!');
    setIsErrorMessage(false);
    setShowSuccessModal(true);
  };

  const getSortIcon = (columnKey: keyof User) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />;
    }

    return sortConfig.direction === 'asc'
      ? <ArrowUp className="h-3.5 w-3.5 text-blue-700" aria-hidden="true" />
      : <ArrowDown className="h-3.5 w-3.5 text-blue-700" aria-hidden="true" />;
  };

  const formatCreatedOn = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Return original string if invalid date
      }
      
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      
      return `${month}-${day}-${year} ${displayHours}:${minutes} ${ampm}`;
    } catch (error) {
      return dateString; // Return original string if parsing fails
    }
  };

  const formatLastLogin = (lastLoginString: string) => {
    // Handle "Never" case
    if (lastLoginString === 'Never' || lastLoginString === 'never') {
      return 'Never';
    }
    
    // Try to format as date
    try {
      const date = new Date(lastLoginString);
      if (isNaN(date.getTime())) {
        return lastLoginString; // Return original string if invalid date
      }
      
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      
      return `${month}-${day}-${year} ${displayHours}:${minutes} ${ampm}`;
    } catch (error) {
      return lastLoginString; // Return original string if parsing fails
    }
  };

  // Handle user status change
  const handleStatusChange = async (user: User) => {
    if (user.role.toLowerCase() === 'admin' && user.accountStatus === 'enabled') {
      setSuccessMessage('Disabling admin users is not allowed');
      setIsErrorMessage(true);
      setShowSuccessModal(true);
      return;
    }

    // Set loading state for this specific user
    setStatusChangeLoading(prev => ({ ...prev, [user.id]: true }));
    
    try {
      const newStatus = user.accountStatus === 'enabled' ? 'disabled' : 'enabled';
      const response = await updateUserStatus(user.id, newStatus);
      
      setSuccessMessage(response.message);
      setIsErrorMessage(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error updating user status:', error);
      setSuccessMessage('Failed to update user status. Please try again.');
      setIsErrorMessage(true);
      setShowSuccessModal(true);
    } finally {
      // Clear loading state for this specific user
      setStatusChangeLoading(prev => ({ ...prev, [user.id]: false }));
    }
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <section className="relative overflow-hidden rounded-2xl bg-slate-950 px-5 py-6 text-white shadow-sm sm:px-7 sm:py-7" aria-labelledby="user-management-title">
        <div className="pointer-events-none absolute inset-y-0 right-0 w-1/2 bg-[radial-gradient(circle_at_center,_rgba(59,130,246,0.22),_transparent_68%)]" />
        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-blue-200">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
              Access control
            </div>
            <h1 id="user-management-title" className="text-2xl font-semibold tracking-tight sm:text-3xl">User management</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">Manage registered system users, account access, and administrative roles.</p>
          </div>
          <button
            type="button"
            onClick={() => setShowAddUserForm(true)}
            title="Create a System User or Moderator"
            className="inline-flex h-11 items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            Add new user
          </button>
        </div>
      </section>

      {/* Add User Form Modal */}
      {showAddUserForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Add New User</h3>
                <button
                  onClick={() => {
                    setShowAddUserForm(false);
                    setFormData({
                      firstName: '',
                      lastName: '',
                      email: '',
                      password: '',
                      role: 'user',
                      status: 'enabled'
                    });
                    setFormErrors({});
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleAddUser} className="space-y-4">
                {/* First Name */}
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleFormChange}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 focus-visible:border-gray-500 ${
                      formErrors.firstName ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter first name"
                  />
                  {formErrors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.firstName}</p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleFormChange}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 focus-visible:border-gray-500 ${
                      formErrors.lastName ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter last name"
                  />
                  {formErrors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.lastName}</p>
                  )}
                </div>

                {/* Email Address */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 focus-visible:border-gray-500 ${
                      formErrors.email ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter email address"
                  />
                  {formErrors.email && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                  )}
                </div>

                {/* Login Password */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    Login Password *
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleFormChange}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 focus-visible:border-gray-500 ${
                      formErrors.password ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter password (minimum 12 characters)"
                  />
                  {formErrors.password && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">Password must be between 12 and 128 characters long</p>
                </div>

                {/* User Type */}
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                    User Type
                  </label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleFormChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 focus-visible:border-gray-500 bg-white"
                  >
                    <option value="user">System User</option>
                    <option value="moderator">Moderator</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">Admin accounts cannot be created.</p>
                </div>

                {/* Account Status */}
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                    Account Status
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleFormChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 focus-visible:border-gray-500 bg-white"
                  >
                    <option value="enabled">Enabled</option>
                    <option value="disabled">Disabled</option>
                  </select>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddUserForm(false);
                      setFormData({
                        firstName: '',
                        lastName: '',
                        email: '',
                        password: '',
                        role: 'user',
                        status: 'enabled'
                      });
                      setFormErrors({});
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-500"
                  >
                    Create User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showUserDetails && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-medium text-gray-900">User Details</h3>
                <button
                  onClick={() => {
                    setShowUserDetails(false);
                    setSelectedUser(null);
                    setEditMode(false);
                    setShowPasswordChange(false);
                    setNewPassword('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* User Information */}
              <div className="space-y-6">
                {/* Basic Info Section */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* First Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name
                      </label>
                      {editMode ? (
                        <input
                          type="text"
                          name="firstName"
                          value={editFormData.firstName}
                          onChange={handleEditFormChange}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 focus-visible:border-gray-500"
                        />
                      ) : (
                        <p className="text-sm text-gray-900 py-2">{editFormData.firstName}</p>
                      )}
                    </div>

                    {/* Last Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      {editMode ? (
                        <input
                          type="text"
                          name="lastName"
                          value={editFormData.lastName}
                          onChange={handleEditFormChange}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 focus-visible:border-gray-500"
                        />
                      ) : (
                        <p className="text-sm text-gray-900 py-2">{editFormData.lastName}</p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      {editMode ? (
                        <input
                          type="email"
                          name="email"
                          value={editFormData.email}
                          onChange={handleEditFormChange}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 focus-visible:border-gray-500"
                        />
                      ) : (
                        <p className="text-sm text-gray-900 py-2">{editFormData.email}</p>
                      )}
                    </div>

                    {/* User ID */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        User ID
                      </label>
                      <p className="text-sm text-gray-900 py-2">{selectedUser.id}</p>
                    </div>

                    {/* User Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        User Type
                      </label>
                      <div className="py-2">{getRoleBadge(selectedUser.role)}</div>
                    </div>


                    {/* Status */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <div className="py-2">{getStatusBadge(selectedUser?.accountStatus || 'disabled')}</div>
                      {editMode && selectedUser.role.toLowerCase() === 'admin' && (
                        <p className="text-xs text-gray-500">Admin status is protected and cannot be disabled.</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Password Change Section */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Password Management</h4>
                  
                  {showPasswordChange ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Current Password
                        </label>
                        <input
                          type="password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          placeholder="Enter current password"
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 focus-visible:border-gray-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          New Password
                        </label>
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Enter new password (minimum 12 characters)"
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 focus-visible:border-gray-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">Password must be between 12 and 128 characters long</p>
                      </div>
                      
                      <div className="flex space-x-3">
                        <button
                          onClick={handlePasswordChange}
                          className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
                        >
                          Change Password
                        </button>
                        <button
                          onClick={() => {
                            setShowPasswordChange(false);
                            setNewPassword('');
                            setCurrentPassword('');
                          }}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowPasswordChange(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      Change Password
                    </button>
                  )}
                </div>

                {/* Last Login Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Last Login</h4>
                  <p className="text-sm text-gray-900">{formatLastLogin(selectedUser.lastLogin)}</p>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  {editMode && (
                    <>
                      <button
                        onClick={toggleEditMode}
                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-500"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveChanges}
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-500"
                      >
                        Save Changes
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    {isErrorMessage ? (
                      <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ) : (
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3">
                    <h3 className={`text-lg font-medium ${isErrorMessage ? 'text-red-900' : 'text-gray-900'}`}>
                      {isErrorMessage ? 'Error' : 'Success'}
                    </h3>
                  </div>
                </div>
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <div className="mb-4">
                <p className={`text-sm ${isErrorMessage ? 'text-red-600' : 'text-gray-600'}`}>{successMessage}</p>
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end">
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className={`px-4 py-2 text-white rounded-md transition-colors ${
                    isErrorMessage 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-gray-900 hover:bg-gray-800'
                  }`}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" role="list" aria-label="User summary">
        <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" role="listitem">
          <div className="flex flex-row-reverse items-start justify-between gap-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><UsersRound className="h-5 w-5" aria-hidden="true" /></span>
            <div className="mr-auto"><p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Total users</p><p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">{loading ? '…' : calculatedStats.totalUsers.toLocaleString()}</p><p className="mt-1 text-xs text-slate-500">Registered accounts</p></div>
          </div>
        </article>
        <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" role="listitem">
          <div className="flex flex-row-reverse items-start justify-between gap-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700"><CheckCircle2 className="h-5 w-5" aria-hidden="true" /></span>
            <div className="mr-auto"><p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Enabled users</p><p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">{loading ? '…' : calculatedStats.activeUsers.toLocaleString()}</p><p className="mt-1 text-xs text-slate-500">Can access the system</p></div>
          </div>
        </article>
        <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" role="listitem">
          <div className="flex flex-row-reverse items-start justify-between gap-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-700"><UserRoundX className="h-5 w-5" aria-hidden="true" /></span>
            <div className="mr-auto"><p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Disabled users</p><p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">{loading ? '…' : calculatedStats.disabledUsers.toLocaleString()}</p><p className="mt-1 text-xs text-slate-500">Access currently restricted</p></div>
          </div>
        </article>
      </div>

      {error && (
        <div className="flex items-start justify-between gap-4 rounded-xl border border-red-200 bg-red-50 p-4 shadow-sm" role="alert">
          <div className="flex items-start gap-3">
            <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />
            <div><p className="text-sm font-semibold text-red-900">Unable to load users</p><p className="mt-1 text-sm text-red-700">{error}</p></div>
          </div>
          <button type="button" onClick={refreshData} className="shrink-0 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2">Try again</button>
        </div>
      )}

      {/* Data Table */}
      <div className="admin-table-shell">
        <div className="flex flex-col gap-4 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><UsersRound className="h-[18px] w-[18px]" aria-hidden="true" /></span>
            <div>
              <h2 className="text-base font-semibold text-slate-950">System user directory</h2>
              <p className="mt-0.5 text-xs text-slate-500" aria-live="polite">Showing {totalItems === 0 ? 0 : startIndex + 1}–{Math.min(endIndex, totalItems)} of {totalItems} users</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setAreFiltersOpen(current => !current)}
            aria-expanded={areFiltersOpen}
            aria-controls="user-management-filters"
            className={`inline-flex h-10 items-center justify-center rounded-xl border px-4 text-sm font-semibold shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${areFiltersOpen || activeFilterCount > 0 ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'}`}
          >
            <SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden="true" />
            Filters
            {activeFilterCount > 0 && <span className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-blue-700 px-1.5 py-0.5 text-[11px] font-bold leading-none text-white">{activeFilterCount}</span>}
            <ChevronDown className={`ml-2 h-4 w-4 transition-transform ${areFiltersOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
          </button>
        </div>

        <div id="user-management-filters" hidden={!areFiltersOpen} className="border-b border-slate-200 bg-slate-50/80 px-4 py-5 sm:px-5">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="flex-1">
              <label htmlFor="user-management-search" className="mb-1.5 block text-xs font-semibold text-slate-600">Search users</label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" aria-hidden="true" />
                <input id="user-management-search" type="search" placeholder="Name, email, or user ID" className={`${filterControlClassName} pl-10`} value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} disabled={loading} />
              </div>
              <p className="mt-1.5 text-xs text-slate-500">Enter at least two characters to search.</p>
            </div>
            <div className="sm:w-48">
              <label htmlFor="user-management-status" className="mb-1.5 block text-xs font-semibold text-slate-600">Status</label>
              <select id="user-management-status" className={filterControlClassName} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)} disabled={loading}>
                <option value="all">All statuses</option>
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
            <div className="sm:w-40">
              <label htmlFor="user-management-items-per-page" className="mb-1.5 block text-xs font-semibold text-slate-600">Rows</label>
              <select id="user-management-items-per-page" className={filterControlClassName} value={itemsPerPage} onChange={(event) => { const newLimit = Number(event.target.value); setItemsPerPage(newLimit); setCurrentPage(1); updateFilters({ limit: newLimit, page: 1 }); }} disabled={loading}>
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div aria-live="polite">{filterError && <p className="flex items-center gap-2 text-sm font-medium text-red-700"><CircleAlert className="h-4 w-4" aria-hidden="true" />{filterError}</p>}</div>
            {activeFilterCount > 0 && <button type="button" onClick={handleClearFilters} disabled={loading} className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"><X className="mr-2 h-4 w-4" aria-hidden="true" />Clear filters</button>}
          </div>
        </div>
        <div className="admin-table-scroll" role="region" aria-label="User management data table" tabIndex={0}>
          <table className="admin-data-table min-w-max">
            <thead>
              <tr>
                <th 
                  onClick={() => handleSort('accountStatus')}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') handleSort('accountStatus'); }}
                  tabIndex={0}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    {getSortIcon('accountStatus')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('fullName')}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') handleSort('fullName'); }}
                  tabIndex={0}
                >
                  <div className="flex items-center space-x-1">
                    <span>Full Name</span>
                    {getSortIcon('fullName')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('email')}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') handleSort('email'); }}
                  tabIndex={0}
                >
                  <div className="flex items-center space-x-1">
                    <span>Email</span>
                    {getSortIcon('email')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('role')}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') handleSort('role'); }}
                  tabIndex={0}
                >
                  <div className="flex items-center space-x-1">
                    <span>User Type</span>
                    {getSortIcon('role')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('lastLogin')}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') handleSort('lastLogin'); }}
                  tabIndex={0}
                >
                  <div className="flex items-center space-x-1">
                    <span>Last Login</span>
                    {getSortIcon('lastLogin')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('createdOn')}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') handleSort('createdOn'); }}
                  tabIndex={0}
                >
                  <div className="flex items-center space-x-1">
                    <span>Created On</span>
                    {getSortIcon('createdOn')}
                  </div>
                </th>
                <th>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <svg className="animate-spin h-8 w-8 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <h3 className="text-lg font-medium text-gray-900 mb-1">Loading users...</h3>
                      <p className="text-gray-500">Please wait while we fetch the user data.</p>
                    </div>
                  </td>
                </tr>
              ) : paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                      </svg>
                      <h3 className="text-lg font-medium text-gray-900 mb-1">No users found</h3>
                      <p className="text-gray-500">Try adjusting your search criteria or filters.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(user.accountStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.fullName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatLastLogin(user.lastLogin)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatCreatedOn(user.createdOn)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleView(user)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          title="View user details"
                        >
                          <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
                        </button>
                        {(user.role.toLowerCase() !== 'admin' || user.accountStatus === 'disabled') && (
                          <button
                            onClick={() => handleStatusChange(user)}
                            disabled={statusChangeLoading[user.id]}
                            className={`p-1.5 rounded-md transition-colors flex items-center justify-center ${
                              user.accountStatus === 'enabled'
                                ? 'bg-red-100 text-red-600 hover:bg-red-200 disabled:bg-red-50 disabled:text-red-400'
                                : 'bg-green-100 text-green-600 hover:bg-green-200 disabled:bg-green-50 disabled:text-green-400'
                            }`}
                            title={user.accountStatus === 'enabled' ? 'Disable user' : 'Enable user'}
                          >
                            {statusChangeLoading[user.id] ? (
                              <FontAwesomeIcon icon={faSpinner} className="w-3 h-3 animate-spin" />
                            ) : user.accountStatus === 'enabled' ? (
                              <FontAwesomeIcon icon={faBan} className="w-3 h-3" />
                            ) : (
                              <FontAwesomeIcon icon={faCheck} className="w-3 h-3" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
        <div className="admin-table-pagination">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => {
                const newPage = Math.max(currentPage - 1, 1);
                setCurrentPage(newPage);
                const filters = { page: newPage, limit: itemsPerPage, search: searchTerm || undefined, status: statusFilter !== 'all' ? statusFilter : undefined };
                updateFilters(filters);
                updateURL(filters);
              }}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => {
                const newPage = Math.min(currentPage + 1, totalPages);
                setCurrentPage(newPage);
                const filters = { page: newPage, limit: itemsPerPage, search: searchTerm || undefined, status: statusFilter !== 'all' ? statusFilter : undefined };
                updateFilters(filters);
                updateURL(filters);
              }}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                <span className="font-medium">{Math.min(endIndex, totalItems)}</span> of{' '}
                <span className="font-medium">{totalItems}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                {/* Previous Button */}
                <button
                  onClick={() => {
                    const newPage = Math.max(currentPage - 1, 1);
                    setCurrentPage(newPage);
                    const filters = { page: newPage, limit: itemsPerPage, search: searchTerm || undefined, status: statusFilter !== 'all' ? statusFilter : undefined };
                    updateFilters(filters);
                    updateURL(filters);
                  }}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>

                {/* Page Numbers */}
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                  
                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  // First page
                  if (startPage > 1) {
                    pages.push(
                      <button
                        key={1}
                        onClick={() => {
                          setCurrentPage(1);
                          const filters = { page: 1, limit: itemsPerPage, search: searchTerm || undefined, status: statusFilter !== 'all' ? statusFilter : undefined };
                          updateFilters(filters);
                          updateURL(filters);
                        }}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(
                        <span key="ellipsis1" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      );
                    }
                  }

                  // Visible pages
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => {
                          setCurrentPage(i);
                          const filters = { page: i, limit: itemsPerPage, search: searchTerm || undefined, status: statusFilter !== 'all' ? statusFilter : undefined };
                          updateFilters(filters);
                          updateURL(filters);
                        }}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          i === currentPage
                            ? 'z-10 bg-gray-900 border-gray-900 text-white'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  // Last page
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(
                        <span key="ellipsis2" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      );
                    }
                    pages.push(
                      <button
                        key={totalPages}
                        onClick={() => {
                          setCurrentPage(totalPages);
                          const filters = { page: totalPages, limit: itemsPerPage, search: searchTerm || undefined, status: statusFilter !== 'all' ? statusFilter : undefined };
                          updateFilters(filters);
                          updateURL(filters);
                        }}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        {totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}

                {/* Next Button */}
                <button
                  onClick={() => {
                    const newPage = Math.min(currentPage + 1, totalPages);
                    setCurrentPage(newPage);
                    const filters = { page: newPage, limit: itemsPerPage, search: searchTerm || undefined, status: statusFilter !== 'all' ? statusFilter : undefined };
                    updateFilters(filters);
                    updateURL(filters);
                  }}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};
